import React, { useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GroupIcon from "@mui/icons-material/Group";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";

const StudentRow = ({ student }) => {
  const fullName = `${student.first_name} ${student.last_name}`.trim();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        borderBottom: "1px solid #E8E8E8",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {student.avatar ? (
          <Avatar sx={{ width: 32, height: 32 }} src={student.avatar} alt={fullName} />
        ) : (
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: "#0172A6",
              fontSize: "14px",
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {fullName?.charAt(0)?.toUpperCase()}
          </Avatar>
        )}
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: "Be Vietnam Pro",
              color: "#202124",
            }}
          >
            {fullName}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "Be Vietnam Pro",
              color: "#6b7280",
            }}
          >
            {student.email}
          </Typography>
        </Box>
      </Box>

      {student.is_completed && (
        <Chip
          label="Completed"
          size="small"
          sx={{
            backgroundColor: "#dcfce7",
            color: "#166534",
            fontSize: "11px",
            height: "22px",
            fontFamily: "Be Vietnam Pro",
          }}
        />
      )}
    </Box>
  );
};

const BatchCard = ({ batch, onToggleComplete }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const {
    id,
    code,
    name,
    course_name,
    batch_type,
    member_count,
    avg_progress,
    completed_count,
    starts_at,
    timestamp,
    students = [],
  } = batch;

  const displayName = name || code;
  // starts_at is a plain date ("2026-08-01"); read it as local midnight, not UTC.
  // Batches without one fall back to when they were created.
  const startDate = starts_at
    ? new Date(`${starts_at}T00:00:00`).toLocaleDateString()
    : timestamp ? new Date(timestamp).toLocaleDateString() : "";
  const isIndividual = batch_type === "individual";

  const handleViewStudent = (courseId) => {
    navigate(`/dashboard/workspace/${courseId}`);
  };

  const handleViewBatchWorkspace = () => {
    // Navigate to batch workspace (uses CourseTemplate, not student Course)
    navigate(`/dashboard/batch/${id}/workspace`);
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  // For individual batches, show single student card style
  if (isIndividual && students.length === 1) {
    const student = students[0];
    const fullName = `${student.first_name} ${student.last_name}`.trim();

    return (
      <Grid item xs={12} md={6} lg={4}>
        <Card
          sx={{
            borderRadius: "10px",
            border: "1px solid #E8E8E8",
            height: "100%",
            boxShadow: "none",
            p: 2,
            mb: 2,
          }}
        >
          <CardContent sx={{ p: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {student.avatar ? (
                  <Avatar sx={{ width: 40, height: 40 }} src={student.avatar} alt={fullName} />
                ) : (
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: "#0172A6",
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {fullName?.charAt(0)?.toUpperCase()}
                  </Avatar>
                )}
                <Box sx={{ ml: 2 }}>
                  <Typography
                    sx={{
                      color: "#202124",
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {fullName}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {student.email}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                {onToggleComplete && (
                  <Box
                    role="button"
                    aria-label={student.is_completed ? "Mark as ongoing" : "Mark as completed"}
                    title={student.is_completed ? "Mark as ongoing" : "Mark as completed"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleComplete(student.course_id, !student.is_completed);
                    }}
                    sx={{ cursor: "pointer", display: "flex" }}
                  >
                    {student.is_completed ? (
                      <CheckCircleOutlineIcon sx={{ color: "#22c55e" }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: "#6b7280" }} />
                    )}
                  </Box>
                )}
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  Started
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {startDate}
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                color: "#202124",
                fontSize: "18px",
                fontWeight: 500,
                mb: 2,
                fontFamily: "Be Vietnam Pro",
              }}
            >
              {course_name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={student.progress || 0}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#E8E8E8",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#0172A6",
                  },
                }}
              />
              <Typography
                sx={{
                  color: "#6b7280",
                  fontSize: "14px",
                  minWidth: "70px",
                  fontFamily: "Be Vietnam Pro",
                }}
              >
                {`${student.progress?.toFixed(0) || 0}%`}
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: "#0172A6",
                color: "white",
                textTransform: "capitalize",
                borderRadius: "6px",
                fontFamily: "Be Vietnam Pro",
                fontSize: "14px",
                mt: 1,
                "&:hover": {
                  backgroundColor: "#0172A6",
                },
              }}
              onClick={() => handleViewStudent(student.course_id)}
            >
              View
            </Button>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // Cohort batch card (expandable)
  return (
    <Grid item xs={12}>
      <Card
        sx={{
          borderRadius: "10px",
          border: "1px solid #E8E8E8",
          boxShadow: "none",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EBF5FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GroupIcon sx={{ color: "#0172A6", fontSize: 28 }} />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      color: "#202124",
                      fontSize: "18px",
                      fontWeight: 600,
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Chip
                    label="Cohort"
                    size="small"
                    sx={{
                      backgroundColor: "#EBF5FF",
                      color: "#0172A6",
                      fontSize: "11px",
                      height: "22px",
                      fontFamily: "Be Vietnam Pro",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {course_name}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              {/* Member count */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    color: "#202124",
                    fontSize: "20px",
                    fontWeight: 600,
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {member_count}
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "12px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  Students
                </Typography>
              </Box>

              {/* Avg Progress */}
              <Box sx={{ textAlign: "center", minWidth: 80 }}>
                <Typography
                  sx={{
                    color: "#202124",
                    fontSize: "20px",
                    fontWeight: 600,
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {avg_progress}%
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "12px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  Avg Progress
                </Typography>
              </Box>

              {/* Completed count */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    color: "#22c55e",
                    fontSize: "20px",
                    fontWeight: 600,
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {completed_count}/{member_count}
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: "12px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  Completed
                </Typography>
              </Box>

              {/* Avatar group */}
              <AvatarGroup max={4} sx={{ ml: 2 }}>
                {students.slice(0, 4).map((student, idx) => (
                  <Avatar
                    key={idx}
                    src={student.avatar}
                    sx={{ width: 32, height: 32, fontSize: "12px" }}
                  >
                    {student.first_name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                ))}
              </AvatarGroup>

              {/* View Workspace button */}
              <Button
                variant="contained"
                size="small"
                onClick={handleViewBatchWorkspace}
                sx={{
                  ml: 2,
                  backgroundColor: "#0172A6",
                  color: "white",
                  textTransform: "none",
                  fontFamily: "Be Vietnam Pro",
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "#015d8a",
                  },
                }}
              >
                View Workspace
              </Button>

              {/* Expand button */}
              <IconButton onClick={handleToggleExpand}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* Progress bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LinearProgress
              variant="determinate"
              value={avg_progress}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: "#E8E8E8",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#0172A6",
                },
              }}
            />
            <Typography
              sx={{
                color: "#6b7280",
                fontSize: "12px",
                fontFamily: "Be Vietnam Pro",
              }}
            >
              Started {startDate}
            </Typography>
          </Box>

          {/* Expanded student list */}
          <Collapse in={expanded}>
            <Box
              sx={{
                mt: 2,
                border: "1px solid #E8E8E8",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#F5F5F5",
                  p: 1.5,
                  borderBottom: "1px solid #E8E8E8",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "Be Vietnam Pro",
                    color: "#202124",
                  }}
                >
                  Students ({students.length})
                </Typography>
              </Box>
              {students.map((student, idx) => (
                <StudentRow key={idx} student={student} />
              ))}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default BatchCard;
